/* =====================================================================
   JS MILLENIUM — Tiroir Filtrer : barre de prix + sections fermées
   ---------------------------------------------------------------------
   1. Barre de prix à deux curseurs (ci-dessous)
   2. À chaque ouverture du tiroir, toutes les sections sont repliées
      — voir en bas du fichier.
   =====================================================================

   1. Barre de prix à deux curseurs
   ---------------------------------------------------------------------
   Horizon n'affiche que deux champs « € min / € max ». Ce script ajoute
   une barre à deux curseurs au-dessus, branchée sur ces mêmes champs :
   le filtrage natif d'Horizon continue de fonctionner tel quel.

   - Les champs natifs restent dans la page (masqués en CSS seulement
     quand la barre est présente : si le script échoue, ils réapparaissent).
   - Le filtre se déclenche au relâchement du curseur, pas à chaque pixel.
   - Horizon redessine les filtres après chaque mise à jour : un observateur
     remet la barre en place automatiquement.
   Styles : injectés par ce script (voir STYLE_ID), doublés dans jsm-filtrer.css.
   Version : v3 (styles forcés)
   ===================================================================== */
(() => {
  /* Pas fin pour pouvoir atteindre les bornes exactes (ex. 20,50 €) ;
     pendant le glissement, la valeur est arrondie à l'euro entier,
     sauf aux extrémités qui restent au centime près. */
  const PAS = 0.01;
  const ECART_MIN = 1;

  /* « 20,50 » → 20.5 */
  const lire = (texte) => {
    const n = parseFloat(String(texte || '').replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  /* 20 → « 20 € » · 20.5 → « 20,50 € » */
  const afficher = (n) =>
    n.toLocaleString('fr-FR', {
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2
    }) + ' €';

  /* 20.5 → « 20,5 » (format attendu par les champs Horizon) */
  const versChamp = (n) => String(n).replace('.', ',');

  /* ---------------------------------------------------------------
     Styles de la barre injectés par le script lui-même, en fin de <head> :
     ils s'appliquent même si jsm-filtrer.css est en cache ou écrasé
     par les styles de champ d'Horizon.
     --------------------------------------------------------------- */
  const STYLE_ID = 'jsm-curseur-styles';
  const CSS = `
    price-facet-component:has(.jsm-curseur) .facets__inputs-wrapper,
    price-facet-component:has(.jsm-curseur) .price-facet__highest-price { display: none !important; }
    .jsm-curseur { --jsm-bouton: 16px; padding: 4px 0 2px; }
    .jsm-curseur .jsm-curseur__piste { position: relative !important; height: 20px !important; }
    .jsm-curseur .jsm-curseur__piste::before {
      content: ''; position: absolute; top: 50%; height: 1px; background: #CFC6B8;
      left: calc(var(--jsm-bouton) / 2); right: calc(var(--jsm-bouton) / 2); transform: translateY(-50%);
    }
    .jsm-curseur .jsm-curseur__plage {
      position: absolute; z-index: 1; top: 50%; height: 3px; background: #1A1613; transform: translateY(-50%);
      left: calc(var(--jsm-bouton) / 2 + (100% - var(--jsm-bouton)) * var(--jsm-a, 0));
      right: calc(var(--jsm-bouton) / 2 + (100% - var(--jsm-bouton)) * (1 - var(--jsm-b, 1)));
    }
    .jsm-curseur input.jsm-curseur__entree::-webkit-slider-runnable-track {
      height: 20px !important; background: transparent !important; border: none !important; box-shadow: none !important;
    }
    .jsm-curseur input.jsm-curseur__entree::-moz-range-track {
      height: 20px !important; background: transparent !important; border: none !important; box-shadow: none !important;
    }
    .jsm-curseur input.jsm-curseur__entree::-moz-range-progress { background: transparent !important; }
    .jsm-curseur input.jsm-curseur__entree::-webkit-slider-thumb {
      -webkit-appearance: none !important; appearance: none !important; pointer-events: auto !important;
      width: 16px !important; height: 16px !important; margin-top: 2px !important;
      border-radius: 50% !important; background: #F7F4EF !important; border: 1px solid #1A1613 !important;
      box-shadow: none !important; cursor: grab;
    }
    .jsm-curseur input.jsm-curseur__entree::-moz-range-thumb {
      pointer-events: auto !important; width: 16px !important; height: 16px !important; box-sizing: border-box !important;
      border-radius: 50% !important; background: #F7F4EF !important; border: 1px solid #1A1613 !important;
      box-shadow: none !important; cursor: grab;
    }
    .jsm-curseur input.jsm-curseur__entree:focus-visible::-webkit-slider-thumb { outline: 1px solid #a8452a; outline-offset: 2px; }
    .jsm-curseur input.jsm-curseur__entree:focus-visible::-moz-range-thumb { outline: 1px solid #a8452a; outline-offset: 2px; }
    .jsm-curseur .jsm-curseur__valeurs {
      display: flex !important; justify-content: space-between; margin-top: 12px;
      font-family: 'Jost', 'Helvetica Neue', sans-serif; font-size: 12.5px; color: #1A1613;
      font-feature-settings: 'tnum' 1, 'lnum' 1;
    }
  `;

  const injecterStyles = () => {
    let el = document.getElementById(STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      el.textContent = CSS;
    }
    document.head.appendChild(el); /* toujours en dernier dans le <head> */
  };

  /* Styles posés directement sur chaque curseur, en !important :
     rien dans le thème ne peut passer par-dessus. */
  const STYLE_CURSEUR = {
    position: 'absolute', inset: '0', 'z-index': '2', display: 'block',
    width: '100%', 'min-width': '0', height: '20px', 'min-height': '0',
    margin: '0', padding: '0', border: 'none', 'border-radius': '0',
    outline: 'none', 'box-shadow': 'none', background: 'transparent',
    'background-color': 'transparent', opacity: '1', 'pointer-events': 'none',
    '-webkit-appearance': 'none', appearance: 'none'
  };
  const forcerStyle = (el) => {
    Object.entries(STYLE_CURSEUR).forEach(([prop, val]) => el.style.setProperty(prop, val, 'important'));
  };

  function monter(composant) {
    if (composant.querySelector('.jsm-curseur')) return;

    const champMin = composant.querySelector('input[name="filter.v.price.gte"]');
    const champMax = composant.querySelector('input[name="filter.v.price.lte"]');
    if (!champMin || !champMax) return;

    /* Bornes exactes envoyées par Shopify (prix mini / maxi de la collection) */
    const borneMin = lire(champMin.dataset.min) ?? 0;
    const borneMax = lire(champMax.dataset.max) ?? 0;
    if (!(borneMax > borneMin)) return;

    const depart = (champ, defaut) => {
      const v = lire(champ.value);
      return v === null ? defaut : Math.min(Math.max(v, borneMin), borneMax);
    };

    const bloc = document.createElement('div');
    bloc.className = 'jsm-curseur';
    bloc.innerHTML =
      '<div class="jsm-curseur__piste">' +
        '<div class="jsm-curseur__plage"></div>' +
        '<input type="range" class="jsm-curseur__entree" data-bord="min" aria-label="Prix minimum">' +
        '<input type="range" class="jsm-curseur__entree" data-bord="max" aria-label="Prix maximum">' +
      '</div>' +
      '<div class="jsm-curseur__valeurs">' +
        '<span data-valeur="min"></span>' +
        '<span data-valeur="max"></span>' +
      '</div>';

    const curseurMin = bloc.querySelector('[data-bord="min"]');
    const curseurMax = bloc.querySelector('[data-bord="max"]');
    const texteMin = bloc.querySelector('[data-valeur="min"]');
    const texteMax = bloc.querySelector('[data-valeur="max"]');

    [curseurMin, curseurMax].forEach((c) => {
      forcerStyle(c);
      c.min = borneMin;
      c.max = borneMax;
      c.step = PAS;
    });
    curseurMin.value = depart(champMin, borneMin);
    curseurMax.value = depart(champMax, borneMax);

    const peindre = () => {
      const a = Number(curseurMin.value);
      const b = Number(curseurMax.value);
      const etendue = borneMax - borneMin;
      bloc.style.setProperty('--jsm-a', (a - borneMin) / etendue);
      bloc.style.setProperty('--jsm-b', (b - borneMin) / etendue);
      texteMin.textContent = afficher(a);
      texteMax.textContent = afficher(b);
    };

    /* Arrondi à l'euro entier, bornes exactes conservées */
    const arrondir = (v) => {
      if (v <= borneMin + 0.5) return borneMin;
      if (v >= borneMax - 0.5) return borneMax;
      return Math.round(v);
    };

    /* Arrondit puis empêche les deux curseurs de se croiser */
    const contenir = (actif) => {
      curseurMin.value = arrondir(Number(curseurMin.value));
      curseurMax.value = arrondir(Number(curseurMax.value));
      const a = Number(curseurMin.value);
      const b = Number(curseurMax.value);
      if (a > b - ECART_MIN) {
        if (actif === curseurMin) curseurMin.value = Math.max(borneMin, b - ECART_MIN);
        else curseurMax.value = Math.min(borneMax, a + ECART_MIN);
      }
    };

    /* Le curseur touché passe au-dessus (utile quand ils se rejoignent) */
    const auPremierPlan = (actif) => {
      curseurMin.style.setProperty('z-index', actif === curseurMin ? '3' : '2', 'important');
      curseurMax.style.setProperty('z-index', actif === curseurMax ? '3' : '2', 'important');
    };

    /* Recopie dans les champs natifs puis lance le filtrage d'Horizon */
    const appliquer = () => {
      const a = Number(curseurMin.value);
      const b = Number(curseurMax.value);
      champMin.value = a <= borneMin ? '' : versChamp(a);
      champMax.value = b >= borneMax ? '' : versChamp(b);
      champMin.dispatchEvent(new Event('input', { bubbles: true }));
      champMin.dispatchEvent(new Event('change', { bubbles: true }));
    };

    [curseurMin, curseurMax].forEach((c) => {
      c.addEventListener('pointerdown', () => auPremierPlan(c));
      c.addEventListener('focus', () => auPremierPlan(c));
      c.addEventListener('input', (e) => {
        e.stopPropagation(); /* Horizon ne doit pas réagir aux curseurs eux-mêmes */
        contenir(c);
        peindre();
      });
      c.addEventListener('change', (e) => {
        e.stopPropagation();
        contenir(c);
        peindre();
        appliquer();
      });
    });

    peindre();
    composant.insertBefore(bloc, composant.firstChild);
  }

  const tout = () => document.querySelectorAll('price-facet-component').forEach(monter);

  let planifie = false;
  const observateur = new MutationObserver(() => {
    if (planifie) return;
    planifie = true;
    requestAnimationFrame(() => {
      planifie = false;
      tout();
    });
  });

  const demarrer = () => {
    injecterStyles();
    tout();
    observateur.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();


/* =====================================================================
   2. Sections repliées à l'ouverture du tiroir
   ---------------------------------------------------------------------
   Horizon ouvre toutes les sections du tiroir par défaut. À chaque
   ouverture, on les replie TOUTES (les filtres actifs restent visibles
   dans les étiquettes en haut du tiroir).
   Pour garder ouvertes les sections qui ont un filtre actif, passer
   GARDER_SECTIONS_ACTIVES à true.
   Pendant que le tiroir est ouvert, on ne touche à rien.
   ===================================================================== */
(() => {
  const GARDER_SECTIONS_ACTIVES = false;

  const aUnFiltreActif = (details) => {
    if (details.querySelector('input[type="checkbox"]:checked')) return true;
    const prix = details.querySelectorAll('input[name^="filter.v.price"]');
    return [...prix].some((champ) => champ.value.trim() !== '');
  };

  const replier = (dialog) => {
    dialog.querySelectorAll('.facets__filters-wrapper details.facets__panel').forEach((details) => {
      const garder = GARDER_SECTIONS_ACTIVES && aUnFiltreActif(details);
      details.open = garder;
      const accordeon = details.closest('accordion-custom');
      if (accordeon && !garder) accordeon.removeAttribute('open');
    });
  };

  const observateur = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      const el = m.target;
      if (
        m.attributeName === 'open' &&
        el.matches &&
        el.matches('dialog.facets--drawer') &&
        el.hasAttribute('open')
      ) {
        replier(el);
      }
    });
  });

  const demarrer = () => {
    observateur.observe(document.body, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();